import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, ShieldCheck, Trash2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SECRET_PERMS = ["get", "list", "set", "delete"];

export default function KeyVault() {
  const { state, refresh } = useAzure();
  const rgs = state?.resource_groups || [];
  const principals = [
    ...(state?.entra_users || []).map((u) => ({ id: u.object_id, name: u.display_name || u.upn, kind: "User" })),
    ...(state?.entra_groups || []).map((g) => ({ id: g.object_id, name: g.name, kind: "Group" })),
  ];
  const [vaults, setVaults] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/azure/keyvault/config"); setVaults(data.vaults); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const after = async () => { await load(); refresh?.(); };

  return (
    <div data-testid="az-key-vault">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-4">
        <KeyRound className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-semibold">Key Vault — secrets & access policies</h2>
      </div>
      <Tabs defaultValue="vaults">
        <TabsList>
          <TabsTrigger value="vaults" data-testid="az-kv-tab-vaults"><KeyRound className="h-4 w-4 mr-1.5" />Vaults</TabsTrigger>
          <TabsTrigger value="secrets" data-testid="az-kv-tab-secrets"><Lock className="h-4 w-4 mr-1.5" />Secrets</TabsTrigger>
          <TabsTrigger value="policies" data-testid="az-kv-tab-policies"><ShieldCheck className="h-4 w-4 mr-1.5" />Access policies</TabsTrigger>
        </TabsList>
        <TabsContent value="vaults" className="mt-4"><Vaults rgs={rgs} vaults={vaults} after={after} /></TabsContent>
        <TabsContent value="secrets" className="mt-4"><Secrets vaults={vaults} after={after} /></TabsContent>
        <TabsContent value="policies" className="mt-4"><Policies vaults={vaults} principals={principals} after={after} /></TabsContent>
      </Tabs>
    </div>
  );
}

function RgSelect({ value, onChange, rgs, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
      <option value="">Select a resource group…</option>
      {rgs.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
    </select>
  );
}

function VaultSelect({ vaults, value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm">
      {vaults.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
    </select>
  );
}

function Vaults({ rgs, vaults, after }) {
  const [name, setName] = useState(""); const [rg, setRg] = useState("");
  const [purge, setPurge] = useState(false); const [rbac, setRbac] = useState(false); const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !rg) return toast.error("Enter a name and pick a resource group.");
    setBusy(true);
    try {
      await api.post("/azure/keyvault/vaults", { name, resource_group: rg, purge_protection: purge, rbac_authorization: rbac });
      toast.success(`Vault '${name}' created`); setName(""); setPurge(false); setRbac(false); await after();
    } catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-border p-4 bg-card">
        <div className="text-sm font-medium mb-3">Create a key vault</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Name</label>
            <Input data-testid="az-kv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="kv-hmg-prod" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Resource group</label>
            <div className="mt-1"><RgSelect testid="az-kv-rg" value={rg} onChange={setRg} rgs={rgs} /></div></div>
        </div>
        <div className="flex flex-wrap gap-6 mt-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={purge} onCheckedChange={setPurge} data-testid="az-kv-purge" /> Purge protection</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={rbac} onCheckedChange={setRbac} data-testid="az-kv-rbac" /> Azure RBAC authorization</label>
        </div>
        <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={create} disabled={busy} data-testid="az-kv-create">
          <Plus className="h-4 w-4" /> {busy ? "Creating…" : "Create vault"}
        </Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-kv-list">
        {vaults.length === 0 && <div className="text-sm text-muted-foreground">No key vaults yet.</div>}
        {vaults.map((v) => (
          <div key={v.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`az-kv-${v.name}`}>
            <KeyRound className="h-4 w-4 text-[#0078d4]" />
            <div className="min-w-0 flex-1"><div className="text-sm font-medium">{v.name}</div>
              <div className="text-xs text-muted-foreground truncate">{v.resource_group} · {v.secrets.length} secret(s) · {v.access_policies.length} policy(ies)</div></div>
            {v.purge_protection && <span className="text-[10px] bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded">Purge-protected</span>}
            {v.rbac_authorization && <span className="text-[10px] bg-[#0078d4]/10 text-[#0078d4] px-2 py-0.5 rounded">RBAC</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Secrets({ vaults, after }) {
  const [vault, setVault] = useState(""); const [name, setName] = useState(""); const [value, setValue] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (!vault && vaults[0]) setVault(vaults[0].id); }, [vaults, vault]);
  const cur = vaults.find((v) => v.id === vault);
  const add = async () => {
    if (!vault || !name.trim() || !value) return toast.error("Pick a vault, secret name and value.");
    setBusy(true);
    try { await api.post("/azure/keyvault/secrets", { vault_id: vault, name, value }); toast.success(`Secret '${name}' stored`); setName(""); setValue(""); await after(); }
    catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  const del = async (n) => { try { await api.post("/azure/keyvault/secrets/delete", { vault_id: vault, name: n }); toast.success("Secret deleted"); await after(); } catch (e) { toast.error(azErr(e)); } };
  if (vaults.length === 0) return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="az-kv-secrets-empty">Create a key vault first.</div>;
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-3"><span className="text-xs text-muted-foreground">Vault</span><VaultSelect vaults={vaults} value={vault} onChange={setVault} testid="az-kv-secret-vault" /></div>
      <div className="rounded-lg border border-border p-4 bg-card grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground">Secret name</label><Input data-testid="az-kv-secret-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="db-password" className="mt-1" /></div>
        <div><label className="text-xs text-muted-foreground">Secret value</label><Input data-testid="az-kv-secret-value" type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder="••••••" className="mt-1" /></div>
        <div className="sm:col-span-2"><Button size="sm" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={add} disabled={busy} data-testid="az-kv-secret-add"><Plus className="h-4 w-4" />{busy ? "Saving…" : "Store secret"}</Button></div>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-kv-secret-list">
        {(cur?.secrets || []).filter((s) => !s.deleted).length === 0 && <div className="text-sm text-muted-foreground">No secrets in this vault.</div>}
        {(cur?.secrets || []).filter((s) => !s.deleted).map((s) => (
          <div key={s.name} className="flex items-center gap-3 border border-border rounded-md px-3 py-2" data-testid={`az-kv-secret-${s.name}`}>
            <Lock className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0 flex-1"><div className="text-sm font-medium">{s.name}</div><div className="text-xs text-muted-foreground font-mono">{s.masked} · v{String(s.version).slice(0, 6)}</div></div>
            <button onClick={() => del(s.name)} className="text-muted-foreground hover:text-red-500" data-testid={`az-kv-secret-del-${s.name}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Policies({ vaults, principals, after }) {
  const [vault, setVault] = useState(""); const [principal, setPrincipal] = useState(""); const [perms, setPerms] = useState(["get", "list"]); const [busy, setBusy] = useState(false);
  useEffect(() => { if (!vault && vaults[0]) setVault(vaults[0].id); }, [vaults, vault]);
  useEffect(() => { if (!principal && principals[0]) setPrincipal(principals[0].id); }, [principals, principal]);
  const cur = vaults.find((v) => v.id === vault);
  const toggle = (p) => setPerms((cs) => cs.includes(p) ? cs.filter((x) => x !== p) : [...cs, p]);
  const add = async () => {
    if (!vault || !principal) return toast.error("Pick a vault and a principal.");
    const pr = principals.find((x) => x.id === principal);
    setBusy(true);
    try { await api.post("/azure/keyvault/access-policies", { vault_id: vault, object_id: principal, principal_name: pr?.name, secret_perms: perms }); toast.success("Access policy added"); await after(); }
    catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  if (vaults.length === 0) return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="az-kv-policies-empty">Create a key vault first.</div>;
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-3"><span className="text-xs text-muted-foreground">Vault</span><VaultSelect vaults={vaults} value={vault} onChange={setVault} testid="az-kv-policy-vault" /></div>
      {cur?.rbac_authorization ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-700" data-testid="az-kv-rbac-note">This vault uses <strong>Azure RBAC</strong> for data access — grant access via role assignments (RBAC blade) instead of access policies.</div>
      ) : (
        <div className="rounded-lg border border-border p-4 bg-card">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Principal</label>
              <select data-testid="az-kv-policy-principal" value={principal} onChange={(e) => setPrincipal(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-2 py-2 text-sm">
                {principals.map((p) => <option key={p.id} value={p.id}>{p.kind}: {p.name}</option>)}
              </select></div>
            <div><label className="text-xs text-muted-foreground">Secret permissions</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SECRET_PERMS.map((p) => (
                  <button key={p} onClick={() => toggle(p)} data-testid={`az-kv-perm-${p}`}
                    className={`text-xs px-2 py-1 rounded border ${perms.includes(p) ? "bg-[#0078d4] text-white border-[#0078d4]" : "border-input text-muted-foreground"}`}>{p}</button>
                ))}
              </div></div>
          </div>
          <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={add} disabled={busy} data-testid="az-kv-policy-add"><Plus className="h-4 w-4" />{busy ? "Adding…" : "Add access policy"}</Button>
        </div>
      )}
      <div className="mt-4 space-y-2" data-testid="az-kv-policy-list">
        {(cur?.access_policies || []).map((p) => (
          <div key={p.object_id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2" data-testid={`az-kv-policy-${p.object_id}`}>
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <div className="min-w-0 flex-1"><div className="text-sm font-medium">{p.principal_name}</div><div className="text-xs text-muted-foreground">secrets: {(p.secrets || []).join(", ") || "none"}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}
